import gsap from "../gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { smoothScroll } from "../../utils/functions";

let setParagraphID = () => null;
let setChapterID = () => null;

const initParagraphTriggers = (currentPart) => {
  ScrollTrigger.matchMedia({
    "(min-width: 900px)": function () {
      document
        .querySelectorAll("#" + currentPart + ".content .card--section")
        .forEach((el) => {
          const id = "#" + el.getAttribute("id");
          ScrollTrigger.create({
            start: () => {
              if (el.classList.contains("is_first_paragraph")) {
                const titleElement = document.getElementById(
                  "mother_" + el.getAttribute("id")
                );
                if (!!titleElement) {
                  return `-${titleElement.offsetHeight} 50%`;
                }
              }
              return "top 50%";
            },
            end: "bottom 50%",
            id,
            trigger: id,
            toggleActions: "restart reverse restart reverse",
            invalidateOnRefresh: false,
            markers: false,
            onEnter: () =>
              handleTimelineParagraphs(el, true, false, setParagraphID),
            onEnterBack: () =>
              handleTimelineParagraphs(el, true, false, setParagraphID),
            onLeave: () =>
              handleTimelineParagraphs(el, false, true, setParagraphID),
            onLeaveBack: () =>
              handleTimelineParagraphs(el, false, true, setParagraphID),
          });
        });
    },
  });
};

const initChapterTriggers = (currentPart) => {
  ScrollTrigger.saveStyles(".storyline .chapters a"); // Puts chapter indicators back in their initial positions after reizing from mobile to desktop.
  ScrollTrigger.matchMedia({
    "(min-width: 900px)": function () {
      // Container for chapter timelines.
      let chapterTimelines = [];

      // Scrolltriggers for the chapter indicators.
      document
        .querySelectorAll("#" + currentPart + ".content.active .chapter")
        .forEach(function (p, i) {
          // Create a new timeline for every chapter.
          chapterTimelines[i] = gsap.timeline({ pause: false });
          let targets =
            ".storyline.active .chapters a:nth-child(" + (i + 1) + ")";
          let previousTarget =
            ".storyline.active .chapters a:nth-child(" + i + ")";

          let progress = document.getElementById("progress-bar");

          let to = gsap.to(targets, {
            scrollTrigger: {
              trigger: "#" + p.id,
              scrub: false,
              start: "top center",
              end: "bottom center",
              toggleActions: "restart none none reverse",
              toggleClass: {
                targets: targets,
                className: "active",
              },
              onToggle: function () {
                setChapterID(p.id);
              },
              onEnter: (self) => {
                progress.style.height = 0 + "%";
                if (document.querySelector(previousTarget)) {
                  chapterTimelines[i].to(targets, {
                    top: 50 + 20,
                  });
                  chapterTimelines[i].to(previousTarget, {
                    top: 20,
                  });
                } else {
                  chapterTimelines[i].to(targets, {
                    top: 50 * i + 20,
                  });
                }
              },
              onLeaveBack: (self) => {
                chapterTimelines[i].to(targets, {
                  bottom: 100,
                  top: "auto",
                });
              },
              onUpdate: (self) => {
                progress.style.height = self.progress.toFixed(3) * 100 + "%";
              },
            },
          });
        });
    },
  });
};

const initEncoreTriggers = (currentPart) => {
  document
    .querySelectorAll("#" + currentPart + ".content .encore")
    .forEach((el) => {
      const id = "#" + el.getAttribute("id");
      ScrollTrigger.create({
        start: () => "top 25%",
        end: "bottom 50%",
        el,
        trigger: el,
        toggleActions: "restart reverse restart reverse",
        invalidateOnRefresh: false,
        markers: false,
        onEnter: () =>
          document.querySelector("html").classList.add("show-encore"),
        onEnterBack: () =>
          document.querySelector("html").classList.add("show-encore"),
        onLeave: () =>
          document.querySelector("html").classList.remove("show-encore"),
        onLeaveBack: () =>
          document.querySelector("html").classList.remove("show-encore"),
      });
    });
};

function handleTimelineParagraphs(paragraph, active, visited, setParagraphID) {
  const images = document.querySelectorAll(".content.active img");

  if (!!images) {
    // Refresh ScrollTrigger for image height shenanigans.
    images.forEach((image) => {
      image.addEventListener("load", () => {
        ScrollTrigger.refresh();
      });
    });
  }

  if (active && !visited) {
    // On enter & on enter back
    setParagraphID(paragraph.id);
  }
}

export const refresh = (
  part_id,
  paragraph_id,
  refreshPart = true,
  fromTheTop = false
) => {
  document.querySelector("html").classList.remove("show-encore");

  /* TODO: Figure out if all of this makes sense. */
  if (refreshPart) {
    killScrollTriggers();
  }
  ScrollTrigger.clearScrollMemory();

  if (refreshPart) {
    initScrollTriggers(part_id);
  }
  /* /TODO */

  // Check if we start from the top, and if the paragraph exists in the DOM.
  // The latter fixes a bug when starting from the mobile view and then switch to the desktop view without navigating first. #21130
  if (
    fromTheTop ||
    !(document.getElementById(paragraph_id) instanceof Element)
  ) {
    // Do nothing.
  } else {
    smoothScroll(document.getElementById(paragraph_id), {
      block: "center",
    }).then(() => undefined);
  }
};

export const toDesktop = (part_id, chapter_id, paragraph_id) => {
  // If no scrolltriggers are found, initialize them.
  // This is relevant when starting from mobile view, and scaling up to desktop.
  if (ScrollTrigger.getAll().length === 0) {
    initScrollTriggers(part_id);
  }

  let targetId = paragraph_id ?? chapter_id ?? part_id;
  // Adding `scrollToParagraph(targetId)` helps with #21104, but scaling manually, still brings you to the top of the page for some reason.
  if (targetId) {
    scrollToParagraph(targetId);
  }
};

const init = (
  currentPart,
  setParagraphIDInit,
  setChapterIDInit,
  startMobile
) => {
  setParagraphID = setParagraphIDInit;
  setChapterID = setChapterIDInit;

  // First condition prevents all triggers from firing at once when starting from mobile view.
  // TODO: Consider initializing mobile specific triggers instead.
  if (!startMobile) {
    initScrollTriggers(currentPart);
  }
};

export const initScrollTriggers = (currentPart) => {
  initChapterTriggers(currentPart);
  initParagraphTriggers(currentPart);
  initEncoreTriggers(currentPart);
  // console.log(
  //   "%c Initialized all (%s) ScrollTriggers.",
  //   "background: #00ff00; color: #000",
  //   ScrollTrigger.getAll().length
  // );
};

export const disableScrollTriggers = () => {
  ScrollTrigger.getAll().forEach((ST) => ST.disable());
  // console.log(
  //   "%c Disabled all (%s) ScrollTriggers.",
  //   "background: orange; color: #000",
  //   ScrollTrigger.getAll().length
  // );
};

export const enableScrollTriggers = () => {
  ScrollTrigger.getAll().forEach((ST) => {
    ST.enable();
    ST.refresh();
  });
  // console.log(
  //   "%c Enabled all (%s) ScrollTriggers.",
  //   "background: cyan; color: #000",
  //   ScrollTrigger.getAll().length
  // );
};

export const killScrollTriggers = () => {
  // console.log(
  //   "%c Killing all (%s) ScrollTriggers.",
  //   "background: #ff0000; color: #000",
  //   ScrollTrigger.getAll().length
  // );
  ScrollTrigger.getAll().forEach((ST) => ST.kill());
};

export const scrollToParagraph = (paragraphID, e = null) => {
  if (e) {
    e.preventDefault();
  }

  smoothScroll(document.getElementById(paragraphID), {
    block: "center",
  }).then(() => undefined);
};

export default init;
